"use client";

import { HomeIcon } from "@heroicons/react/20/solid";
import { skipToken, useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import { orpc } from "@/orpc/client";

interface BreadcrumbPage {
	name: string;
	href: string;
	current: boolean;
}

// Check if a string looks like a UUID
function isUuid(str: string): boolean {
	const uuidPattern =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	return uuidPattern.test(str);
}

// Extract IDs from URL path based on known patterns
function extractIdsFromPath(pathname: string) {
	const segments = pathname.split("/").filter(Boolean);

	let appId: string | undefined;
	let linkId: string | undefined;
	let partnerId: string | undefined;

	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i];
		const nextSegment = segments[i + 1];

		if (segment === "apps" && nextSegment && isUuid(nextSegment)) {
			appId = nextSegment;
		}
		if (segment === "links" && nextSegment && isUuid(nextSegment)) {
			linkId = nextSegment;
		}
		if (segment === "partners" && nextSegment && isUuid(nextSegment)) {
			partnerId = nextSegment;
		}
	}

	return { appId, linkId, partnerId };
}

/**
 * Breadcrumbs component that displays navigation path based on current route.
 * Uses DaisyUI colors for consistent styling.
 * Queries entity names directly for IDs in the URL.
 */
export function Breadcrumbs() {
	const location = useLocation();

	// Extract IDs from URL
	const { appId, linkId, partnerId } = useMemo(
		() => extractIdsFromPath(location.pathname),
		[location.pathname],
	);

	// Query entities only when we have their IDs
	const { data: app } = useQuery(
		orpc.apps.detail.queryOptions({
			input: appId ? { id: appId } : skipToken,
		}),
	);

	const { data: link } = useQuery(
		orpc.referralLinks.detail.queryOptions({
			input: linkId ? { id: linkId } : skipToken,
		}),
	);

	const { data: partner } = useQuery(
		orpc.partners.detail.queryOptions({
			input: partnerId ? { id: partnerId } : skipToken,
		}),
	);

	// Build ID -> name mapping
	const idToName = useMemo(() => {
		const map = new Map<string, string>();
		if (appId && app?.name) {
			map.set(appId, app.name);
		}
		if (linkId && link?.slug) {
			map.set(linkId, link.slug);
		}
		if (partnerId && partner?.name) {
			map.set(partnerId, partner.name);
		}
		return map;
	}, [appId, app?.name, linkId, link?.slug, partnerId, partner?.name]);

	const pages = useMemo<BreadcrumbPage[]>(() => {
		const pathname = location.pathname;
		const segments = pathname.split("/").filter(Boolean);

		const breadcrumbs: BreadcrumbPage[] = [];
		let currentPath = "";

		for (let i = 0; i < segments.length; i++) {
			const segment = segments[i];
			currentPath += `/${segment}`;

			// Skip dashboard as we have home icon for that
			if (segment === "dashboard") continue;

			const isLast = i === segments.length - 1;

			// Determine display name
			let name: string;

			if (isUuid(segment)) {
				// Look up entity name from our map
				const entityName = idToName.get(segment);
				if (!entityName) {
					// Skip this segment if we don't have a name for it yet
					continue;
				}
				name = entityName;
			} else {
				// Convert kebab-case to Title Case
				name = segment
					.split("-")
					.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
					.join(" ");
			}

			breadcrumbs.push({
				name,
				href: currentPath,
				current: isLast,
			});
		}

		return breadcrumbs;
	}, [location.pathname, idToName]);

	return (
		<nav aria-label="Breadcrumb" className="flex">
			<ol className="flex items-center space-x-4">
				<li>
					<div>
						<Link
							to="/dashboard"
							className="text-base-content/50 hover:text-base-content/70 transition-colors"
						>
							<HomeIcon aria-hidden="true" className="size-5 shrink-0" />
							<span className="sr-only">Home</span>
						</Link>
					</div>
				</li>
				{pages.map((page) => (
					<li key={page.href}>
						<div className="flex items-center">
							<svg
								fill="currentColor"
								viewBox="0 0 20 20"
								aria-hidden="true"
								className="size-5 shrink-0 text-base-content/30"
							>
								<path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
							</svg>
							<Link
								to={page.href}
								aria-current={page.current ? "page" : undefined}
								className={`ml-4 text-sm font-medium transition-colors ${
									page.current
										? "text-base-content/70"
										: "text-base-content/50 hover:text-base-content/70"
								}`}
							>
								{page.name}
							</Link>
						</div>
					</li>
				))}
			</ol>
		</nav>
	);
}
